import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef, startTransition } from 'react';
import { Company, Group, GroupWithChildren, GroupSelectionState, IndustryType, Unit, UnitFilterData, HierarchicalGroupNode, UserPermissions } from '@/types';
import { getCompanies, getGroupsForCompany, getBulkGroupsForCompanies, getUnits } from '@/lib/api';
import { cache, invalidateFleetCaches } from '@/lib/cache';
import { useAuth } from './AuthContext';
import { useRBAC } from './RBACContext';
import { MODEL_LABELS, INDUSTRY_TYPE_ORDER } from '@/lib/constants';
import { trackFilterChanged } from '@/lib/analytics';

export interface SavedFilters {
  companyIds: string[];
  groupIds: string[];
  industryTypes: IndustryType[];
  models: string[];
  statuses: string[];
}

export interface FilterContextType {
  selectedCompanyIds: string[];
  selectedGroupIds: string[];
  selectedIndustryTypes: IndustryType[];
  selectedModels: string[];
  selectedStatuses: string[];
  companies: Company[];
  groups: Group[];
  groupTree: GroupWithChildren[];
  // Memoized hierarchy maps for O(1) lookup in search
  companyGroupsMap: Map<string, Set<string>>;
  groupDescendantsMap: Map<string, Set<string>>;
  // Centralized units state - pages push units here for global filter access
  // Accepts either full Unit[] or minimal UnitFilterData[] for filter calculations
  units: (Unit | UnitFilterData)[];
  setUnits: (units: (Unit | UnitFilterData)[]) => void;
  setSelectedCompanyIds: (ids: string[]) => void;
  setSelectedGroupIds: (ids: string[]) => void;
  setSelectedIndustryTypes: (types: IndustryType[]) => void;
  setSelectedModels: (models: string[]) => void;
  setSelectedStatuses: (statuses: string[]) => void;
  toggleGroupWithCascade: (groupId: string) => void;
  getGroupSelectionState: (group: GroupWithChildren) => GroupSelectionState;
  flushCache: () => void;
  /**
   * Re-read companies, groups and units from the API right now, bypassing every
   * cache layer. Call it from Settings after a write that changes fleet structure
   * (unit reassignment, group/company create/edit/delete) so the Map, the filter
   * sidebar and Reports agree with what Settings just showed the operator —
   * without waiting for a TTL or a page reload. Filter SELECTIONS are preserved.
   */
  refreshFleetData: () => Promise<void>;
  isLoading: boolean;
  // Filter restoration (for cluster tooltip quick filters)
  hasSavedFilters: boolean;
  saveFiltersForRestore: () => void;
  restoreSavedFilters: () => void;
  // Hierarchical grouping for sidebar
  buildHierarchicalGroups: (units: Unit[], permissions: UserPermissions) => HierarchicalGroupNode[];
  // Sidebar expand-all trigger (for cluster tooltip zoom-to actions)
  shouldExpandAll: boolean;
  setShouldExpandAll: (value: boolean) => void;
}

export const FilterContext = createContext<FilterContextType | undefined>(undefined);

// Helper function to build tree from flat group list
export function buildGroupTree(groups: Group[]): GroupWithChildren[] {
  const lookup = new Map<string, GroupWithChildren>();
  const roots: GroupWithChildren[] = [];

  // Create nodes with children array
  groups.forEach(g => {
    lookup.set(g.id, { ...g, children: [], level: 0 });
  });

  // Build parent-child relationships
  groups.forEach(g => {
    const node = lookup.get(g.id)!;
    if (g.parent_group_id && lookup.has(g.parent_group_id)) {
      const parent = lookup.get(g.parent_group_id)!;
      parent.children!.push(node);
      node.level = (parent.level || 0) + 1;
    } else {
      roots.push(node);
    }
  });

  return roots;
}

/**
 * Helper to validate and restore industry type selection from cache.
 * Validates cached selection against available company industries and auto-selects if invalid.
 *
 * @param cachedSelection - Previously cached industry type selection (may be stale)
 * @param companies - Current list of companies to validate against
 * @param setIndustryTypes - State setter for industry types
 * @param cacheIndustryTypes - Optional callback to update cache when auto-selecting
 * @param logPrefix - Prefix for console logs to identify call site
 * @returns Object with success boolean and the industries that have data
 */
export function validateAndRestoreIndustryTypes(
  cachedSelection: IndustryType[] | null,
  companies: Company[],
  setIndustryTypes: (types: IndustryType[]) => void,
  cacheIndustryTypes?: (types: IndustryType[]) => void,
  logPrefix: string = '[FilterContext]'
): { success: boolean; industriesWithData: IndustryType[] } {
  // Need companies to validate against
  if (!companies.length) {
    return { success: false, industriesWithData: [] };
  }

  // Get valid industries from companies
  const validIndustryTypes = new Set<string>(INDUSTRY_TYPE_ORDER);
  const industriesWithData = [...new Set(
    companies
      .map((a: Company) => a.industry)
      .filter((industry): industry is IndustryType =>
        typeof industry === 'string' && validIndustryTypes.has(industry)
      )
  )];

  if (!industriesWithData.length) {
    return { success: false, industriesWithData: [] };
  }

  // Check if we have a cached selection (including intentionally empty array)
  // null/undefined = no cache, auto-select all
  // empty array [] = user cleared all selections, keep empty
  // array with items = validate and restore
  if (cachedSelection !== null && cachedSelection !== undefined) {
    // User intentionally cleared all selections - respect their choice
    if (cachedSelection.length === 0) {
      setIndustryTypes([]);
      console.log(`✅ ${logPrefix} Restored empty industry selection (user cleared all)`);
      return { success: true, industriesWithData };
    }

    // User has selections - validate them
    const availableIndustries = new Set(industriesWithData);
    const validCachedSelection = cachedSelection.filter(type => availableIndustries.has(type));

    if (validCachedSelection.length > 0) {
      setIndustryTypes(validCachedSelection);
      console.log(`✅ ${logPrefix} Restored valid cached industry type selection:`, validCachedSelection);

      // If some cached types were invalid, update the cache
      if (validCachedSelection.length !== cachedSelection.length) {
        const invalidTypes = cachedSelection.filter(t => !availableIndustries.has(t));
        console.warn(`⚠️ ${logPrefix} Removed invalid cached industry types:`, invalidTypes);
        cacheIndustryTypes?.(validCachedSelection);
      }
      return { success: true, industriesWithData };
    }

    // All cached types are invalid - auto-select
    console.warn(`⚠️ ${logPrefix} All cached industry types invalid, auto-selecting:`, industriesWithData);
  }

  // No cached selection (null/undefined) - auto-select all available industries
  setIndustryTypes(industriesWithData);
  cacheIndustryTypes?.(industriesWithData);
  console.log(`✅ ${logPrefix} Auto-selected industries with data:`, industriesWithData);
  return { success: true, industriesWithData };
}

// Helper function to get all descendant IDs recursively
// Exported for use in hierarchical search
export function getAllDescendantIds(groupId: string, groupTree: GroupWithChildren[]): string[] {
  const findGroup = (groups: GroupWithChildren[]): GroupWithChildren | null => {
    for (const group of groups) {
      if (group.id === groupId) return group;
      if (group.children) {
        const found = findGroup(group.children);
        if (found) return found;
      }
    }
    return null;
  };

  const collectIds = (group: GroupWithChildren): string[] => {
    const ids: string[] = [];
    if (group.children) {
      group.children.forEach(child => {
        ids.push(child.id);
        ids.push(...collectIds(child));
      });
    }
    return ids;
  };

  const group = findGroup(groupTree);
  return group ? collectIds(group) : [];
}

// Helper function to build hierarchical group structure for sidebar
export function buildHierarchicalGroupStructure(
  units: Unit[],
  companies: Company[],
  groups: Group[],
  permissions: UserPermissions
): HierarchicalGroupNode[] {
  const result: HierarchicalGroupNode[] = [];

  // Create lookup maps
  const companyMap = new Map(companies.map(a => [a.id, a]));
  const groupMap = new Map(groups.map(g => [g.id, g]));

  // Group units by company → group → site hierarchy
  const hierarchyMap = new Map<string, Map<string, Map<string, Unit[]>>>();

  units.forEach(unit => {
    const companyId = unit.company_id;
    // Use company-specific unassigned ID to keep unassigned units under their company
    const unassignedId = `__unassigned_${companyId}__`;
    const groupId = unit.primary_group_id || unassignedId;
    const isUnassigned = !unit.primary_group_id;
    const group = !isUnassigned ? groupMap.get(groupId) : null;

    // Get or create company level
    if (!hierarchyMap.has(companyId)) {
      hierarchyMap.set(companyId, new Map());
    }
    const companyLevel = hierarchyMap.get(companyId)!;

    // Determine group and site
    let resolvedGroupId = groupId;
    let siteId: string | null = null;

    // If group has a parent, it's a child group (site-level)
    if (group && group.parent_group_id) {
      const parent = groupMap.get(group.parent_group_id);
      if (parent) {
        resolvedGroupId = parent.id;
        siteId = groupId; // Child group becomes site
      }
    } else if (group && !group.parent_group_id) {
      // Top-level group with no parent - treat as parent group only
      resolvedGroupId = groupId;
      siteId = null; // No separate site level for top-level groups
    } else if (isUnassigned) {
      // Unassigned units: groupId is the company-specific unassigned ID
      resolvedGroupId = unassignedId;
      siteId = null;
    }

    // Get or create group level
    if (!companyLevel.has(resolvedGroupId)) {
      companyLevel.set(resolvedGroupId, new Map());
    }
    const groupLevel = companyLevel.get(resolvedGroupId)!;

    // Get or create site level (only if siteId is different from group)
    const finalSiteId = siteId || resolvedGroupId; // Use resolvedGroupId as site if no separate site
    if (!groupLevel.has(finalSiteId)) {
      groupLevel.set(finalSiteId, []);
    }
    groupLevel.get(finalSiteId)!.push(unit);
  });

  // Build hierarchical node structure based on permissions
  hierarchyMap.forEach((groupMap2, companyId) => {
    const company = companyMap.get(companyId);
    if (!company) return;

    // Calculate total units for this company
    const companyUnits: Unit[] = [];
    groupMap2.forEach(siteMap => {
      siteMap.forEach(siteUnits => companyUnits.push(...siteUnits));
    });

    // If user can see companies, create company-level node
    if (permissions.canSeeCompanies) {
      const companyNode: HierarchicalGroupNode = {
        id: `company_${companyId}`,
        name: company.name,
        level: 'company',
        unitCount: companyUnits.length,
        companyId,
        units: companyUnits,
        children: [],
      };

      // Add group-level children
      groupMap2.forEach((siteMap, groupId) => {
        const parentGroup = groupMap.get(groupId);
        const groupUnits: Unit[] = [];
        siteMap.forEach(siteUnits => groupUnits.push(...siteUnits));

        if (permissions.canSeeGroups) {
          const groupNode: HierarchicalGroupNode = {
            id: `group_${groupId}`,
            name: parentGroup?.name || 'Unassigned',
            level: 'group',
            unitCount: groupUnits.length,
            groupId,
            units: groupUnits,
            children: [],
          };

          // Add site-level children (only if different from parent group)
          siteMap.forEach((siteUnits, siteId) => {
            // Skip creating duplicate site node if siteId === groupId
            if (siteId !== groupId) {
              const siteGroup = groupMap.get(siteId);
              const siteNode: HierarchicalGroupNode = {
                id: `site_${siteId}`,
                name: siteGroup?.name || 'Unassigned',
                level: 'site',
                unitCount: siteUnits.length,
                siteId,
                units: siteUnits,
              };
              groupNode.children!.push(siteNode);
            }
          });

          companyNode.children!.push(groupNode);
        } else {
          // Skip group level, add sites directly to company
          siteMap.forEach((siteUnits, siteId) => {
            // Skip creating duplicate site node if siteId === groupId
            if (siteId !== groupId) {
              const siteGroup = groupMap.get(siteId);
              const siteNode: HierarchicalGroupNode = {
                id: `site_${siteId}`,
                name: siteGroup?.name || 'Unassigned',
                level: 'site',
                unitCount: siteUnits.length,
                siteId,
                units: siteUnits,
              };
              companyNode.children!.push(siteNode);
            }
          });
        }
      });

      result.push(companyNode);
    } else if (permissions.canSeeGroups) {
      // Skip company level, show groups as top level
      groupMap2.forEach((siteMap, groupId) => {
        const parentGroup = groupMap.get(groupId);
        const groupUnits: Unit[] = [];
        siteMap.forEach(siteUnits => groupUnits.push(...siteUnits));

        const groupNode: HierarchicalGroupNode = {
          id: `group_${groupId}`,
          name: parentGroup?.name || 'Unassigned',
          level: 'group',
          unitCount: groupUnits.length,
          groupId,
          units: groupUnits,
          children: [],
        };

        // Add site-level children
        siteMap.forEach((siteUnits, siteId) => {
          const siteGroup = groupMap.get(siteId);
          const siteNode: HierarchicalGroupNode = {
            id: `site_${siteId}`,
            name: siteGroup?.name || 'Unassigned',
            level: 'site',
            unitCount: siteUnits.length,
            siteId,
            units: siteUnits,
          };
          groupNode.children!.push(siteNode);
        });

        result.push(groupNode);
      });
    } else {
      // Show only sites (flattest structure)
      groupMap2.forEach(siteMap => {
        siteMap.forEach((siteUnits, siteId) => {
          const siteGroup = groupMap.get(siteId);
          const siteNode: HierarchicalGroupNode = {
            id: `site_${siteId}`,
            name: siteGroup?.name || 'Unassigned',
            level: 'site',
            unitCount: siteUnits.length,
            siteId,
            units: siteUnits,
          };
          result.push(siteNode);
        });
      });
    }
  });

  // Sort helper: put "Unassigned" at the bottom, then alphabetical for the rest
  const sortWithUnassignedLast = (nodes: HierarchicalGroupNode[]) => {
    return nodes.sort((a, b) => {
      // Check both name AND id for unassigned detection
      const aIsUnassigned = a.name === 'Unassigned' || a.id.includes('__unassigned');
      const bIsUnassigned = b.name === 'Unassigned' || b.id.includes('__unassigned');

      if (aIsUnassigned && !bIsUnassigned) return 1; // a goes to bottom
      if (!aIsUnassigned && bIsUnassigned) return -1; // b goes to bottom
      // Alphabetical sort for non-Unassigned items
      return a.name.localeCompare(b.name);
    });
  };

  // Apply sorting to all levels
  result.forEach(node => {
    if (node.children && node.children.length > 0) {
      node.children = sortWithUnassignedLast(node.children);
      // Also sort grandchildren
      node.children.forEach(child => {
        if (child.children && child.children.length > 0) {
          child.children = sortWithUnassignedLast(child.children);
        }
      });
    }
  });

  // Sort top-level nodes as well
  return sortWithUnassignedLast(result);
}

// Cache key base names - MUST include userId to prevent cross-user data leakage
// See AuthContext.signOut() which clears these on logout
export const CACHE_KEY_BASE = {
  companies: 'company_filter_companies',
  groups: 'company_filter_groups',
  units: 'company_filter_units', // Cached minimal unit data for instant filter counts
  unitsTimestamp: 'company_filter_units_timestamp', // Track when units were cached
  selectedCompanyIds: 'company_filter_selected_companies',
  selectedGroupIds: 'company_filter_selected_groups',
  selectedModels: 'company_filter_selected_models',
  selectedStatuses: 'company_filter_selected_statuses',
  timestamp: 'company_filter_timestamp',
};

/**
 * Generate user-specific cache key to prevent cross-user data leakage.
 * If no userId provided, returns null (don't cache without user context).
 */
export function getUserCacheKey(baseKey: string, userId: string | undefined): string | null {
  if (!userId) return null;
  return `${baseKey}_${userId}`;
}

/**
 * Safe localStorage getter with user validation.
 * Returns null if:
 * - No userId (can't validate ownership)
 * - No cached data exists
 * - JSON parsing fails
 */
export function getCachedData<T>(baseKey: string, userId: string | undefined): T | null {
  const cacheKey = getUserCacheKey(baseKey, userId);
  if (!cacheKey) return null;

  try {
    const cached = localStorage.getItem(cacheKey);
    return cached ? JSON.parse(cached) : null;
  } catch {
    return null;
  }
}

/**
 * Clear all FilterContext-related caches to free up space.
 */
function clearFilterCaches(): void {
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && (key.includes('company_filter_') || key.includes('filter_selections_'))) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach(key => localStorage.removeItem(key));
}

/**
 * Safe localStorage setter with user-specific key.
 * Does nothing if no userId (prevents user-agnostic cache pollution).
 * On quota exceeded, clears old filter caches and retries once.
 */
export function setCachedData<T>(baseKey: string, userId: string | undefined, data: T): void {
  const cacheKey = getUserCacheKey(baseKey, userId);
  if (!cacheKey) return;

  try {
    localStorage.setItem(cacheKey, JSON.stringify(data));
  } catch (error) {
    // On quota exceeded, clear old caches and retry
    if (error instanceof DOMException && error.name === 'QuotaExceededError') {
      clearFilterCaches();
      try {
        localStorage.setItem(cacheKey, JSON.stringify(data));
      } catch {
        // Still failing - skip caching silently
      }
    }
  }
}

/**
 * How long the localStorage MIRROR of companies is treated as authoritative
 * before this provider re-asks the API.
 *
 * This is only a paint shortcut — src/lib/cache.ts owns real freshness — but while
 * it holds, no request is made at all. At 24 hours that made a company (and every
 * unit reassigned into it) invisible to the Map and Reports for a full day.
 */
export const CACHE_DURATION_MS = 5 * 60 * 1000;

/**
 * Fold a refreshed entity list into an existing filter selection.
 *
 * The Map and Reports filter units against an ALLOW-LIST of selected ids, so an
 * entity the user has never had the chance to tick — a production created this
 * morning, a company a unit was just moved into — is not merely absent from the
 * filter UI: every unit on it silently disappears from the map. Newly appeared ids
 * are therefore selected automatically. Ids the user already saw and deliberately
 * unticked are left alone, and ids that no longer exist are dropped.
 */
export function mergeSelectionWithNewEntities(
  currentSelection: string[],
  previousIds: string[],
  nextIds: string[]
): string[] {
  const alreadyKnown = new Set(previousIds);
  const stillValid = new Set(nextIds);
  const kept = currentSelection.filter(id => stillValid.has(id));
  const newlyAppeared = nextIds.filter(id => !alreadyKnown.has(id));
  return [...new Set([...kept, ...newlyAppeared])];
}
export function useFilters() {
  const context = useContext(FilterContext);
  if (!context) {
    throw new Error('useFilters must be used within FilterProvider');
  }
  return context;
}
